// reportConfig.js — Report definitions, column schemas, and data fetchers.
// One config drives the hub cards, the /reports/:slug pages, and exports.
//
// Column types: text | money | int | days | decimal | date | enum | status
// - money:  "(RM)" lives in the header only; cells are bare comma numbers,
//           right-aligned; 0 muted gray-400; negatives red-700.
// - int:    whole-number counts ("22", not "22.00").
// - days:   leave-day balances — integer when whole, one decimal otherwise.
// - enum:   title-cased via formatEnumLabel.
// - status: chip — Active green, Resigned/Terminated gray.
import { supabase } from '../../supabaseClient';
import { formatEnumLabel } from '../../utils/formatters';

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// ---------------------------------------------------------------------------
// Fetchers — each returns an array of plain objects keyed by column key.
// ctx: { month, year, dept, emp, type, status, companyId, allCompanies }
// ---------------------------------------------------------------------------

const scoped = (query, { companyId, allCompanies }) =>
  allCompanies ? query : query.eq('company_id', companyId);

const fetchPayrollSummary = async (ctx) => {
  let query = supabase
    .from('payroll_records')
    .select('id, employee_id, month, year, basic_pay, fixed_allowance, ot_weekday, ot_saturday, ot_sunday, ot_public_holiday, dynamic_allowances, gross_pay, epf_employee, socso_employee, eis_employee, skbbk_employee, total_deductions, advance_deduction, net_pay, total_cost, employees!inner(full_name, department, employee_id)')
    .eq('year', ctx.year)
    .eq('month', ctx.month);

  query = scoped(query, ctx);
  if (ctx.dept !== 'all') query = query.eq('employees.department', ctx.dept);
  if (ctx.emp !== 'all') query = query.eq('employee_id', ctx.emp);

  const { data, error } = await query;
  if (error) throw error;

  return (data || []).map((row) => ({
    employeeId: row.employees?.employee_id || row.employee_id,
    name: row.employees?.full_name || '',
    department: row.employees?.department || '',
    basicPay: row.basic_pay || 0,
    fixedAllowance: row.fixed_allowance || 0,
    totalOT: (row.ot_weekday || 0) + (row.ot_saturday || 0) + (row.ot_sunday || 0) + (row.ot_public_holiday || 0),
    dynamicAllowances: row.dynamic_allowances || 0,
    grossPay: row.gross_pay || 0,
    epfEmp: row.epf_employee || 0,
    socsoEmp: row.socso_employee || 0,
    eisEmp: row.eis_employee || 0,
    skbbkEmp: row.skbbk_employee || 0,
    deductions: row.total_deductions || 0,
    advanceDed: row.advance_deduction || 0,
    netPay: row.net_pay || 0,
    totalCost: row.total_cost || 0,
  }));
};

const fetchStatutory = async (ctx) => {
  let query = supabase
    .from('payroll_records')
    .select('employee_id, epf_employee, epf_employer, socso_employee, socso_employer, eis_employee, eis_employer, skbbk_employee, employees!inner(full_name, employee_id, ic_number, epf_number, socso_number, eis_number)')
    .eq('year', ctx.year)
    .eq('month', ctx.month);

  query = scoped(query, ctx);

  const { data, error } = await query;
  if (error) throw error;

  return (data || []).map((row) => ({
    employeeId: row.employees?.employee_id || row.employee_id,
    name: row.employees?.full_name || '',
    icNumber: row.employees?.ic_number || '',
    epfNo: row.employees?.epf_number || '',
    socsoNo: row.employees?.socso_number || '',
    eisNo: row.employees?.eis_number || '',
    epfEmployee: row.epf_employee || 0,
    epfEmployer: row.epf_employer || 0,
    socsoEmployee: row.socso_employee || 0,
    socsoEmployer: row.socso_employer || 0,
    eisEmployee: row.eis_employee || 0,
    eisEmployer: row.eis_employer || 0,
    skbbkEmployee: row.skbbk_employee || 0,
  }));
};

const fetchLeaveBalances = async (ctx) => {
  let query = supabase
    .from('leave_entitlements')
    .select('employee_id, leave_type_code, total_entitled, used, pending, remaining, carried_forward, employees!inner(full_name, employee_id, company_id, gender)')
    .eq('year', ctx.year);

  if (!ctx.allCompanies) query = query.eq('employees.company_id', ctx.companyId);
  if (ctx.emp !== 'all') query = query.eq('employees.employee_id', ctx.emp);
  if (ctx.type !== 'all') query = query.eq('leave_type_code', ctx.type);

  const { data, error } = await query;
  if (error) throw error;

  return (data || [])
    .filter((row) => {
      // Maternity is only applicable to female employees; paternity only to male.
      const gender = row.employees?.gender;
      if (row.leave_type_code === 'maternity' && gender !== 'female') return false;
      if (row.leave_type_code === 'paternity' && gender !== 'male') return false;
      // Hide maternity/paternity rows that have never been used and carry no entitlement.
      if (row.leave_type_code === 'maternity' || row.leave_type_code === 'paternity') {
        const entitled = row.total_entitled || 0;
        const used = row.used || 0;
        const pending = row.pending || 0;
        const carried = row.carried_forward || 0;
        if (entitled === 0 && used === 0 && pending === 0 && carried === 0) return false;
      }
      return true;
    })
    .map((row) => {
    // Remaining is always derived: Entitled - Used - Pending. The stored
    // `remaining` column can drift (e.g. after manual entitlement edits),
    // so we recompute and log any row where the identity doesn't hold.
    const entitled = row.total_entitled || 0;
    const used = row.used || 0;
    const pending = row.pending || 0;
    const remaining = Math.round((entitled - used - pending) * 100) / 100;
    const dbRemaining = row.remaining || 0;
    if (Math.abs(remaining - dbRemaining) > 0.001) {
      console.warn(
        `[LeaveBalanceReport] Remaining mismatch: ${row.employees?.full_name || row.employee_id} ` +
        `${row.leave_type_code} ${row.year || ''} — entitled ${entitled} - used ${used} - pending ${pending} ` +
        `= ${remaining}, but DB remaining = ${dbRemaining}`
      );
    }

    return {
      employeeId: row.employees?.employee_id || row.employee_id,
      name: row.employees?.full_name || '',
      leaveType: row.leave_type_code || '',
      entitled,
      used,
      pending,
      remaining,
      carriedForward: row.carried_forward || 0,
    };
  });
};

const fetchEmployeeDirectory = async (ctx) => {
  let query = supabase
    .from('employees')
    .select('employee_id, full_name, ic_number, phone, email, department, position, date_joined, status, bank_name, bank_account, epf_number, socso_number, eis_number, income_tax_number');

  query = scoped(query, ctx);
  if (ctx.dept !== 'all') query = query.eq('department', ctx.dept);
  if (ctx.status !== 'all') query = query.eq('status', ctx.status);

  const { data, error } = await query;
  if (error) throw error;

  return (data || []).map((row) => ({
    employeeId: row.employee_id || '',
    name: row.full_name || '',
    icNumber: row.ic_number || '',
    phone: row.phone || '',
    email: row.email || '',
    department: row.department || '',
    position: row.position || '',
    dateJoined: row.date_joined || '',
    status: row.status || '',
    bankName: row.bank_name || '',
    bankAccount: row.bank_account || '',
    epfNo: row.epf_number || '',
    socsoNo: row.socso_number || '',
    eisNo: row.eis_number || '',
    taxNo: row.income_tax_number || '',
  }));
};

const fetchAttendanceDays = async (ctx) => {
  // Build date range for the month
  const startDate = `${ctx.year}-${String(ctx.month).padStart(2, '0')}-01`;
  const nextMonth = ctx.month === 12 ? 1 : ctx.month + 1;
  const nextYear = ctx.month === 12 ? ctx.year + 1 : ctx.year;
  const endDate = `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`;

  let query = supabase
    .from('attendance_days')
    .select('employee_id, date, first_in, last_out, worked_min, late_min, early_out_min, approved_ot_min, actual_ot_min, payable_ot_min, manual_count, trip_count, flags, employees!inner(full_name, employee_id, is_driver)')
    .gte('date', startDate)
    .lt('date', endDate);

  query = scoped(query, ctx);
  if (ctx.dept !== 'all') query = query.eq('employees.department', ctx.dept);
  if (ctx.emp !== 'all') query = query.eq('employee_id', ctx.emp);
  if (ctx.driver !== 'all') query = query.eq('employees.is_driver', ctx.driver === 'yes');

  const { data, error } = await query;
  if (error) throw error;

  // Group by employee
  const empMap = {};
  for (const row of (data || [])) {
    const eid = row.employee_id;
    if (!empMap[eid]) {
      empMap[eid] = {
        employeeId: row.employees?.employee_id || eid,
        name: row.employees?.full_name || '',
        isDriver: row.employees?.is_driver || false,
        daysWorked: 0, lateDays: 0, lateMin: 0, earlyOutDays: 0, earlyOutMin: 0,
        approvedOtMin: 0, actualOtMin: 0, payableOtMin: 0,
        manualEntries: 0, tripCount: 0, noGpsCount: 0, outOfZoneCount: 0,
      };
    }
    const e = empMap[eid];
    if (row.worked_min > 0) e.daysWorked++;
    if (row.late_min > 0) { e.lateDays++; e.lateMin += row.late_min; }
    if (row.early_out_min > 0) { e.earlyOutDays++; e.earlyOutMin += row.early_out_min; }
    e.approvedOtMin += row.approved_ot_min || 0;
    e.actualOtMin += row.actual_ot_min || 0;
    e.payableOtMin += row.payable_ot_min || 0;
    e.manualEntries += row.manual_count || 0;
    e.tripCount += row.trip_count || 0;
    const flags = row.flags || [];
    if (flags.includes('no_gps')) e.noGpsCount++;
    if (flags.includes('out_of_zone')) e.outOfZoneCount++;
  }

  return Object.values(empMap).map((e) => ({
    ...e,
    // Convert minutes to hours with 2 decimals for readability
    lateMinHr: Math.round(e.lateMin / 60 * 100) / 100,
    earlyOutMinHr: Math.round(e.earlyOutMin / 60 * 100) / 100,
    approvedOtHr: Math.round(e.approvedOtMin / 60 * 100) / 100,
    actualOtHr: Math.round(e.actualOtMin / 60 * 100) / 100,
    payableOtHr: Math.round(e.payableOtMin / 60 * 100) / 100,
  }));
};

const fetchAttendanceLateness = async (ctx) => {
  let query = supabase
    .from('payroll_records')
    .select('employee_id, attendance_data, employees!inner(full_name, employee_id)')
    .eq('year', ctx.year)
    .eq('month', ctx.month);

  query = scoped(query, ctx);

  const { data, error } = await query;
  if (error) throw error;

  return (data || []).map((row) => {
    const attendance = row.attendance_data || {};
    let worked = 0, absent = 0, mc = 0, al = 0, ul = 0, el = 0, ph = 0;
    let late = 0, earlyOut = 0, otHours = 0;

    Object.values(attendance).forEach((day) => {
      const status = day.status || '';
      const hasWork = (day.timeIn && day.timeOut) || (day.hoursWorked > 0) || (day.regular > 0);

      if (status === 'Absent') absent++;
      else if (status === 'MC') mc++;
      else if (status === 'AL') al++;
      else if (status === 'UL' || status === 'Unpaid Leave') ul++;
      else if (status === 'EL') el++;
      else if (status === 'PH') ph++;
      else if (['CL', 'MTL', 'PTL'].includes(status)) {
        // Other paid leave types — no dedicated column in this report
      } else if (hasWork) worked++;

      if ((day.lateIn || 0) > 0) late++;
      if ((day.earlyOut || 0) > 0) earlyOut++;
      otHours += (day.weekdayOT || 0) + (day.satHours || 0) + (day.sunHours || 0) + (day.phHours || 0);
    });

    return {
      employeeId: row.employees?.employee_id || row.employee_id,
      name: row.employees?.full_name || '',
      worked, absent, mc, al, ul, el, ph,
      late, earlyOut,
      otHours: Number(otHours.toFixed(2)),
    };
  });
};

// ---------------------------------------------------------------------------
// Report definitions
// ---------------------------------------------------------------------------

export const REPORTS = [
  {
    slug: 'payroll-summary',
    name: 'Monthly Payroll Summary',
    description: 'Complete payroll breakdown per employee including earnings, deductions, and net pay.',
    filters: ['month', 'year', 'department', 'employee'],
    fetch: fetchPayrollSummary,
    hasTotals: true,
    columns: [
      { key: 'employeeId', label: 'EMPLOYEE ID', type: 'text', sticky: true, width: 110 },
      { key: 'name', label: 'NAME', type: 'text', sticky: true, width: 200 },
      { key: 'department', label: 'DEPARTMENT', type: 'text' },
      { key: 'basicPay', label: 'BASIC PAY (RM)', type: 'money' },
      { key: 'fixedAllowance', label: 'FIXED ALLOW. (RM)', type: 'money' },
      { key: 'totalOT', label: 'TOTAL OT (RM)', type: 'money' },
      { key: 'dynamicAllowances', label: 'DYN. ALLOW. (RM)', type: 'money' },
      { key: 'grossPay', label: 'GROSS PAY (RM)', type: 'money' },
      { key: 'epfEmp', label: 'EPF EMP (RM)', type: 'money' },
      { key: 'socsoEmp', label: 'SOCSO EMP (RM)', type: 'money' },
      { key: 'eisEmp', label: 'EIS EMP (RM)', type: 'money' },
      { key: 'skbbkEmp', label: 'SKBBK EMP (RM)', type: 'money' },
      { key: 'deductions', label: 'DEDUCTIONS (RM)', type: 'money' },
      { key: 'advanceDed', label: 'ADVANCE (RM)', type: 'money' },
      { key: 'netPay', label: 'NET PAY (RM)', type: 'money' },
      { key: 'totalCost', label: 'TOTAL COST (RM)', type: 'money' },
    ],
  },
  {
    slug: 'statutory-contributions',
    name: 'Statutory Contribution Report',
    description: 'EPF, SOCSO, and EIS contributions for employee and employer.',
    filters: ['month', 'year'],
    fetch: fetchStatutory,
    hasTotals: true,
    columns: [
      { key: 'employeeId', label: 'EMPLOYEE ID', type: 'text', sticky: true, width: 110 },
      { key: 'name', label: 'NAME', type: 'text', sticky: true, width: 200 },
      { key: 'icNumber', label: 'IC NUMBER', type: 'text' },
      { key: 'epfNo', label: 'EPF NO', type: 'text' },
      { key: 'socsoNo', label: 'SOCSO NO', type: 'text' },
      { key: 'eisNo', label: 'EIS NO', type: 'text' },
      { key: 'epfEmployee', label: 'EPF EMPLOYEE (RM)', type: 'money' },
      { key: 'epfEmployer', label: 'EPF EMPLOYER (RM)', type: 'money' },
      { key: 'socsoEmployee', label: 'SOCSO EMPLOYEE (RM)', type: 'money' },
      { key: 'socsoEmployer', label: 'SOCSO EMPLOYER (RM)', type: 'money' },
      { key: 'eisEmployee', label: 'EIS EMPLOYEE (RM)', type: 'money' },
      { key: 'eisEmployer', label: 'EIS EMPLOYER (RM)', type: 'money' },
      { key: 'skbbkEmployee', label: 'SKBBK EMPLOYEE (RM)', type: 'money' },
    ],
  },
  {
    slug: 'leave-balances',
    name: 'Leave Balance Report',
    description: 'Current leave entitlements, usage, and remaining balances.',
    filters: ['year', 'leave_type', 'employee'],
    fetch: fetchLeaveBalances,
    hasTotals: true,
    columns: [
      { key: 'employeeId', label: 'EMPLOYEE ID', type: 'text', sticky: true, width: 110 },
      { key: 'name', label: 'NAME', type: 'text', sticky: true, width: 200 },
      { key: 'leaveType', label: 'LEAVE TYPE', type: 'enum' },
      { key: 'entitled', label: 'ENTITLED', type: 'days' },
      { key: 'used', label: 'USED', type: 'days' },
      { key: 'pending', label: 'PENDING', type: 'days' },
      { key: 'remaining', label: 'REMAINING', type: 'days' },
      { key: 'carriedForward', label: 'CARRIED FWD', type: 'days' },
    ],
  },
  {
    slug: 'employee-directory',
    name: 'Employee Directory',
    description: 'Complete employee master data with contact and statutory information.',
    filters: ['department', 'status'],
    fetch: fetchEmployeeDirectory,
    hasTotals: false,
    columns: [
      { key: 'employeeId', label: 'EMPLOYEE ID', type: 'text', sticky: true, width: 110 },
      { key: 'name', label: 'NAME', type: 'text', sticky: true, width: 200 },
      { key: 'icNumber', label: 'IC NUMBER', type: 'text' },
      { key: 'phone', label: 'PHONE', type: 'text' },
      { key: 'email', label: 'EMAIL', type: 'text' },
      { key: 'department', label: 'DEPARTMENT', type: 'text' },
      { key: 'position', label: 'POSITION', type: 'text' },
      { key: 'dateJoined', label: 'DATE JOINED', type: 'date' },
      { key: 'status', label: 'STATUS', type: 'status' },
      { key: 'bankName', label: 'BANK NAME', type: 'text' },
      { key: 'bankAccount', label: 'BANK ACCOUNT', type: 'text' },
      { key: 'epfNo', label: 'EPF NO', type: 'text' },
      { key: 'socsoNo', label: 'SOCSO NO', type: 'text' },
      { key: 'eisNo', label: 'EIS NO', type: 'text' },
      { key: 'taxNo', label: 'TAX NO', type: 'text' },
    ],
  },
  {
    slug: 'attendance-days',
    name: 'Attendance Days Report',
    description: 'Days worked, late/early minutes, OT approved vs payable, manual entries, trip count, and geofence flags per employee.',
    filters: ['month', 'year', 'department', 'employee', 'driver'],
    fetch: fetchAttendanceDays,
    hasTotals: true,
    columns: [
      { key: 'employeeId', label: 'EMPLOYEE ID', type: 'text', sticky: true, width: 110 },
      { key: 'name', label: 'NAME', type: 'text', sticky: true, width: 200 },
      { key: 'isDriver', label: 'DRIVER', type: 'text' },
      { key: 'daysWorked', label: 'DAYS WORKED', type: 'int' },
      { key: 'lateDays', label: 'LATE DAYS', type: 'int' },
      { key: 'lateMinHr', label: 'LATE (HRS)', type: 'decimal' },
      { key: 'earlyOutDays', label: 'EARLY OUT DAYS', type: 'int' },
      { key: 'earlyOutMinHr', label: 'EARLY OUT (HRS)', type: 'decimal' },
      { key: 'approvedOtHr', label: 'APPR OT (HRS)', type: 'decimal' },
      { key: 'actualOtHr', label: 'ACTUAL OT (HRS)', type: 'decimal' },
      { key: 'payableOtHr', label: 'PAYABLE OT (HRS)', type: 'decimal' },
      { key: 'manualEntries', label: 'MANUAL ENTRIES', type: 'int' },
      { key: 'tripCount', label: 'TRIPS', type: 'int' },
      { key: 'noGpsCount', label: 'NO GPS', type: 'int' },
      { key: 'outOfZoneCount', label: 'OUT OF ZONE', type: 'int' },
    ],
  },
  {
    slug: 'attendance-lateness',
    name: 'Attendance & Lateness Summary',
    description: 'Days worked, absent, MC, AL, OT hours, and lateness counts.',
    filters: ['month', 'year'],
    fetch: fetchAttendanceLateness,
    hasTotals: true,
    columns: [
      { key: 'employeeId', label: 'EMPLOYEE ID', type: 'text', sticky: true, width: 110 },
      { key: 'name', label: 'NAME', type: 'text', sticky: true, width: 200 },
      { key: 'worked', label: 'WORKED', type: 'int' },
      { key: 'absent', label: 'ABSENT', type: 'int' },
      { key: 'mc', label: 'MC', type: 'int', tooltip: 'Medical Leave (MC)' },
      { key: 'al', label: 'AL', type: 'int', tooltip: 'Annual Leave (AL)' },
      { key: 'ul', label: 'UL', type: 'int', tooltip: 'Unpaid Leave (UL)' },
      { key: 'el', label: 'EL', type: 'int', tooltip: 'Emergency Leave (EL)' },
      { key: 'ph', label: 'PH', type: 'int', tooltip: 'Public Holiday (PH)' },
      { key: 'late', label: 'LATE', type: 'int' },
      { key: 'earlyOut', label: 'EARLY OUT', type: 'int' },
      { key: 'otHours', label: 'OT HOURS', type: 'decimal' },
    ],
  },
];

export const getReportBySlug = (slug) => REPORTS.find((r) => r.slug === slug) || null;

const NUMERIC_TYPES = new Set(['money', 'int', 'days', 'decimal']);

export const isNumericColumn = (col) => NUMERIC_TYPES.has(col.type);

// Display-value formatter for a single cell (screen + PDF export).
export const formatCell = (col, val) => {
  if (val === null || val === undefined || val === '') return '-';
  switch (col.type) {
    case 'money': {
      const n = Number(val) || 0;
      const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      return n < 0 ? `-${abs}` : abs;
    }
    case 'int':
      return String(Math.round(Number(val) || 0));
    case 'days': {
      const n = Number(val) || 0;
      return Number.isInteger(n) ? String(n) : n.toFixed(1);
    }
    case 'decimal': {
      const n = Number(val) || 0;
      return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    }
    case 'date':
      return val; // formatted by the caller (formatDate) — kept raw for flexibility
    case 'enum':
      return formatEnumLabel(val);
    default:
      return val;
  }
};

// Reports hidden per role: the Employee Directory exposes IC/bank/statutory
// numbers, so it is not offered to finance (admin/super_admin only).
// Consumed by ReportsDashboard (card list) and ReportPage (direct URL guard).
export const ROLE_HIDDEN_REPORTS = {
  finance: ['employee-directory'],
};
